import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Loader2, Users, Eye } from 'lucide-react';
import toast from '../../../utils/toast';
import { formatDate } from '../../../utils/dateUtils';
import DebouncedSearchInput from '../../../components/UI/DebouncedSearchInput';
import { InfoBadge } from '../../../components/UI/InfoBadge';
import {
  getDepartmentBadgeColor,
  getDepartmentShortName,
} from '../../../config/departmentConfig';

/**
 * Directory of employees who have submitted an assessment result
 * (inventory-agnostic — serves both Big Five and DISC tabs).
 *
 * No score data is rendered here. Clicking an employee delegates to the
 * parent (Talent Insights shell) which performs an optimistic API call
 * to decide whether to open the result modal at the justification gate
 * or directly at the result step (the latter when the same viewer is
 * still inside the 30-minute grace window from a prior justified
 * access). `pendingStaffId` is the staff_id whose request is in flight,
 * so we can show a spinner on just that row's button.
 *
 * `initialSearch` lets the parent pre-fill the search box when navigating
 * here from elsewhere — currently used by the Aggregate tab's
 * "small department → use Individual view" callout, which routes HR to
 * this tab pre-filtered to that one department. Changes to `initialSearch`
 * are honoured every time it changes so repeated drill-downs work.
 *
 * Props
 * -----
 * - `fetchDirectory`   API method returning `{ employees: [...] }`
 *                      (e.g. `adminGetBigFiveDirectory`).
 * - `assessmentName`   Display name used in the empty state copy.
 */
const AssessmentDirectory = ({
  fetchDirectory,
  assessmentName,
  onSelectEmployee,
  pendingStaffId,
  initialSearch = '',
}) => {
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState(initialSearch);

  // Sync from parent when the drill-down target changes (e.g. user
  // clicks "use Individual view" on a second department).
  useEffect(() => {
    setSearch(initialSearch || '');
  }, [initialSearch]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await fetchDirectory();
      setEmployees(data?.employees || []);
    } catch (err) {
      toast.error('Could not load the employee directory.');
    } finally {
      setLoading(false);
    }
  }, [fetchDirectory]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter((e) =>
      [e.display_name, e.staff_id, e.department, e.designation]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q))
    );
  }, [employees, search]);

  return (
    <div className="space-y-4">
      <div>
        <DebouncedSearchInput
          value={search}
          onSearch={setSearch}
          placeholder="Search by name, ID, department"
          theme="admin"
          className="w-full sm:max-w-xl"
        />
      </div>

      {/* Body */}
      {loading ? (
        <div className="py-16 flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-red-600 animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-12 text-center">
          <Users className="w-10 h-10 text-gray-300 mx-auto" />
          <h3 className="mt-3 text-base font-semibold text-gray-900">
            {employees.length === 0 ? 'No submissions yet' : 'No matches'}
          </h3>
          <p className="mt-1 text-sm text-gray-600 max-w-md mx-auto">
            {employees.length === 0
              ? `Once colleagues complete the ${assessmentName}, they will appear here.`
              : 'Try a different name, staff ID, department, or designation.'}
          </p>
        </div>
      ) : (
        <>
          <div className="md:hidden space-y-2">
            {filtered.map((emp) => {
              const isPending = pendingStaffId === emp.staff_id;
              return (
                <article
                  key={emp.staff_id}
                  className="rounded-xl border border-gray-200 bg-white p-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {emp.display_name || '—'}
                      </p>
                      <p className="mt-0.5 text-xs text-gray-500">{emp.staff_id}</p>
                    </div>
                    <span className="text-xs text-gray-600 whitespace-nowrap flex-shrink-0">
                      {emp.submitted_at ? formatDate(emp.submitted_at) : '—'}
                    </span>
                  </div>
                  {(emp.department || emp.designation) && (
                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                      {emp.department && (
                        <InfoBadge
                          color={getDepartmentBadgeColor(emp.department)}
                          label={getDepartmentShortName(emp.department)}
                          title={emp.department}
                        />
                      )}
                      {emp.designation && (
                        <span className="text-xs text-gray-600 line-clamp-1">
                          {emp.designation}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="mt-3 flex justify-end">
                    <button
                      type="button"
                      onClick={() => onSelectEmployee(emp)}
                      disabled={!!pendingStaffId}
                      className="btn btn-admin-outline btn-sm inline-flex items-center gap-1.5"
                      aria-label={`View result for ${emp.display_name || emp.staff_id}`}
                    >
                      {isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                      ) : (
                        <Eye className="w-4 h-4" aria-hidden="true" />
                      )}
                      View result
                    </button>
                  </div>
                </article>
              );
            })}
          </div>

          <div className="hidden md:block overflow-hidden rounded-xl border border-gray-200">
            <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left text-xs uppercase tracking-wider text-gray-500">
                <th className="px-4 py-3 font-semibold">Employee</th>
                <th className="px-4 py-3 font-semibold hidden md:table-cell">Department</th>
                <th className="px-4 py-3 font-semibold hidden lg:table-cell">Designation</th>
                <th className="px-4 py-3 font-semibold">Submitted</th>
                <th className="px-4 py-3 font-semibold text-right">Action</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filtered.map((emp) => (
                <tr key={emp.staff_id} className="hover:bg-gray-50 transition">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{emp.display_name || '—'}</div>
                    <div className="text-xs text-gray-500">{emp.staff_id}</div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    {emp.department ? (
                      <InfoBadge
                        color={getDepartmentBadgeColor(emp.department)}
                        label={getDepartmentShortName(emp.department)}
                        title={emp.department}
                      />
                    ) : (
                      <span className="text-gray-700">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell text-gray-700">
                    {emp.designation || '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    {emp.submitted_at ? formatDate(emp.submitted_at) : '—'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => onSelectEmployee(emp)}
                      disabled={!!pendingStaffId}
                      className="icon-button-admin"
                      title={`View result for ${emp.display_name || emp.staff_id}`}
                      aria-label={`View result for ${emp.display_name || emp.staff_id}`}
                    >
                      {pendingStaffId === emp.staff_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                      ) : (
                        <Eye className="w-4 h-4" aria-hidden="true" />
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default AssessmentDirectory;
